import { createKnownError } from "@/configuration/errorHandler/createKnownError";
import { handleKnownError } from "@/configuration/errorHandler/handleKnownError";
import { EDomainErrorCode } from "@/configuration/errorHandler/types";
import { getLocale } from "@/configuration/lang/langSlice";
import { api } from "@/services/config/config";
import { EResponseStatus } from "@/services/config/types";
import { syncKPIDataVisualizationFromServer } from "@/store/chat/KPIDataVisualizationPreference/KPIDataVisualizationPreference.slice";
import { findLastHumanMessageBefore } from "@/store/chat/utils";
import { RootState } from "@/store/store";
import Logger from "@/utils/logger";
import { keysToSnakeCase } from "@/utils/mappers/toSnakeCaseMapper";

import { TSaveUserKPIPreferencePayload, TSaveUserKPIPreferenceRequest, TSaveUserKPIPreferenceResponse } from "./types";

const LOGGER_TAG = "Service - KPIDataVisualisationPreferenceApi";


export const kpiDataVisualisationPreferenceApi = api.injectEndpoints({
    endpoints: (builder) => ({
        saveUserKPIDataVisualisationPreference: builder.mutation<TSaveUserKPIPreferenceResponse, TSaveUserKPIPreferencePayload>({
            async queryFn({ messageId, hiddenColumns, columnOrderby }, { getState }, _, baseQuery) {
                
                const state = (getState() as RootState);
                const answer = state.chat.entities[messageId];
                const question = findLastHumanMessageBefore(state, { fromStore: { messageId }});

                if(!question || !answer) { 

                    Logger.error(LOGGER_TAG, "Invalid question or answer", { question, answer, messageId });
                    const error = createKnownError({
                        code: EDomainErrorCode.DOMAIN_ERROR,
                        message: "common.error",
                    });

                    handleKnownError(error);

                    return { error: error };
                }
                
                const body: TSaveUserKPIPreferenceRequest = {
                    question: question?.content || null,
                    xAxis: answer.data_visualization?.x_axis || "",
                    currentLanguage: getLocale(state)!,
                    columnOrderby,
                    hiddenColumns,
                };

                const response =  await baseQuery({
                    method: 'PUT',
                    url: "user/preferences/kpis",
                    body: keysToSnakeCase(body),
                });

                const result = response.data as TSaveUserKPIPreferenceResponse
                if(result?.status !== EResponseStatus.SUCCESS) {
                    Logger.error(LOGGER_TAG, "Internal status is failure", result)
                    const error = createKnownError({
                        code: EDomainErrorCode.DOMAIN_ERROR,
                        message: "common.error",
                        originalError: result.error,
                    });
                    handleKnownError(error);
                    return { error }
                }

                const queryResponse = { ...result, messageId };
                return { data: queryResponse };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const response = await queryFulfilled;

                    const data = response.data.data;
                    const { x_axis, column_orderby, hidden_columns } = data;

                    dispatch(syncKPIDataVisualizationFromServer({
                      messageId: response.data.messageId,
                      data: {
                        default_data_visualization: "table", // INFO: Currently only supports tables
                        x_axis: x_axis,
                        settings: {
                          column_orderby,
                          hidden_columns
                        }
                      }
                    }))
                } catch (err) {
                    Logger.error(LOGGER_TAG, "Mutation failed", err);
                }
            }
        })
    })
});

export const {
    useSaveUserKPIDataVisualisationPreferenceMutation
} = kpiDataVisualisationPreferenceApi;